import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { pdvApi, documentsApi, trainingApi, safetyChecksApi } from '../lib/api';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Store,
  MapPin,
  Users,
  Phone,
  Mail,
  FileText,
  GraduationCap,
  ClipboardCheck,
  Calendar,
  Maximize,
  Building2,
  Receipt,
  AtSign,
  Folder
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';

const StatusBadge = ({ status }) => {
  const variants = {
    valid: 'bg-emerald-100 text-emerald-800',
    expiring: 'bg-amber-100 text-amber-800',
    expired: 'bg-rose-100 text-rose-800',
    completed: 'bg-emerald-100 text-emerald-800',
    requires_action: 'bg-rose-100 text-rose-800'
  };

  const labels = {
    valid: 'Valido',
    expiring: 'In Scadenza',
    expired: 'Scaduto',
    completed: 'Completato',
    requires_action: 'Richiede Azione'
  };

  return (
    <Badge className={variants[status] || variants.valid}>
      {labels[status] || status}
    </Badge>
  );
};

const DocumentsList = ({ documents, pdvId }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between mb-4">
      <p className="text-sm text-muted-foreground">{documents.length} documenti</p>
      <Link to={`/pdv/${pdvId}/documents`}>
        <Button variant="outline" size="sm">
          <Folder className="w-4 h-4 mr-2" />
          Gestione Cartelle
        </Button>
      </Link>
    </div>
    {documents.length > 0 ? (
      documents.slice(0, 5).map((doc) => (
        <div key={doc.id} className="flex items-center justify-between p-4 border border-border rounded-sm data-row">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">{doc.name}</p>
              <p className="text-sm text-muted-foreground">{doc.category}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {doc.expiry_date && (
              <span className="text-sm text-muted-foreground">
                Scade: {new Date(doc.expiry_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </span>
            )}
            <StatusBadge status={doc.status} />
          </div>
        </div>
      ))
    ) : (
      <div className="empty-state py-8">
        <FileText className="empty-state-icon" />
        <p className="empty-state-title">Nessun documento</p>
        <Link to={`/pdv/${pdvId}/documents`}>
          <Button variant="outline" className="mt-4">Gestisci Documenti</Button>
        </Link>
      </div>
    )}
    {documents.length > 5 && (
      <Link to={`/pdv/${pdvId}/documents`}>
        <Button variant="link" className="w-full">
          Vedi tutti i {documents.length} documenti
        </Button>
      </Link>
    )}
  </div>
);

const TrainingsList = ({ trainings }) => (
  <div className="space-y-3">
    {trainings.length > 0 ? (
      trainings.map((t) => (
        <div key={t.id} className="flex items-center justify-between p-4 border border-border rounded-sm data-row">
          <div className="flex items-center gap-3">
            <GraduationCap className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">{t.course_name}</p>
              <p className="text-sm text-muted-foreground">{t.employee_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {t.expiry_date && (
              <span className="text-sm text-muted-foreground">
                Scade: {new Date(t.expiry_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </span>
            )}
            <StatusBadge status={t.status} />
          </div>
        </div>
      ))
    ) : (
      <div className="empty-state py-8">
        <GraduationCap className="empty-state-icon" />
        <p className="empty-state-title">Nessuna formazione</p>
        <Link to="/training">
          <Button variant="outline" className="mt-4">Aggiungi formazione</Button>
        </Link>
      </div>
    )}
  </div>
);

const ChecksList = ({ checks }) => (
  <div className="space-y-3">
    {checks.length > 0 ? (
      checks.map((c) => (
        <div key={c.id} className="flex items-center justify-between p-4 border border-border rounded-sm data-row">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">{c.template_name}</p>
              <p className="text-sm text-muted-foreground">
                {new Date(c.inspection_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })} - {c.inspector_name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold">{c.conformity_score}%</p>
              <Progress value={c.conformity_score} className="w-20 h-1.5" />
            </div>
            <StatusBadge status={c.status} />
          </div>
        </div>
      ))
    ) : (
      <div className="empty-state py-8">
        <ClipboardCheck className="empty-state-icon" />
        <p className="empty-state-title">Nessun controllo</p>
        <Link to="/safety-checks">
          <Button variant="outline" className="mt-4">Esegui controllo</Button>
        </Link>
      </div>
    )}
  </div>
);

export default function PDVDetailPage() {
  const { id } = useParams();
  const { setCurrentPdvId } = useCurrentPdv();
  const [pdv, setPdv] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auto-select this UL globally when entering its detail page
  useEffect(() => {
    if (id) setCurrentPdvId(id);
  }, [id, setCurrentPdvId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pdvRes, docsRes, trainRes, checksRes] = await Promise.all([
          pdvApi.getOne(id),
          documentsApi.getAll({ pdv_id: id }),
          trainingApi.getAll({ pdv_id: id }),
          safetyChecksApi.getAll({ pdv_id: id })
        ]);
        setPdv(pdvRes.data);
        setDocuments(docsRes.data);
        setTrainings(trainRes.data);
        setChecks(checksRes.data);
      } catch (error) {
        toast.error("Errore nel caricamento dell'Unità Locale");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!pdv) {
    return (
      <div className="empty-state">
        <Store className="empty-state-icon" />
        <p className="empty-state-title">Unità locale non trovata</p>
        <Link to="/pdv">
          <Button className="mt-4">Torna alla lista</Button>
        </Link>
      </div>
    );
  }

  const expiredDocs = documents.filter(d => d.status === 'expired').length;
  const expiringDocs = documents.filter(d => d.status === 'expiring').length;
  const expiredTrainings = trainings.filter(t => t.status === 'expired').length;
  const expiringTrainings = trainings.filter(t => t.status === 'expiring').length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="pdv-detail-page"
    >
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link to="/pdv">
          <Button variant="ghost" size="icon" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="page-title">{pdv.name}</h1>
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>{pdv.address}, {pdv.city} ({pdv.region})</span>
          </div>
        </div>
        <Badge variant={pdv.status === 'active' ? 'default' : 'secondary'} className="text-sm">
          {pdv.status === 'active' ? 'Attivo' : 'Inattivo'}
        </Badge>
      </div>

      {/* Company Info */}
      {(pdv.company_name || pdv.vat_number || pdv.fiscal_code || pdv.pec) && (
        <Card className="mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Dati Aziendali
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {pdv.company_name && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Ragione Sociale</p>
                  <p className="font-medium">{pdv.company_name}</p>
                </div>
              )}
              {pdv.vat_number && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Partita IVA</p>
                  <p className="font-medium">{pdv.vat_number}</p>
                </div>
              )}
              {pdv.fiscal_code && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Codice Fiscale</p>
                  <p className="font-medium">{pdv.fiscal_code}</p>
                </div>
              )}
              {pdv.pec && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">PEC</p>
                  <p className="font-medium">{pdv.pec}</p>
                </div>
              )}
              {pdv.sdi_code && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Codice SDI</p>
                  <p className="font-medium">{pdv.sdi_code}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="hover-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{pdv.employee_count}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Dipendenti</p>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Maximize className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{pdv.area_sqm || '-'}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">mq</p>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Phone className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium truncate">{pdv.phone || '-'}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Telefono</p>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium truncate">{pdv.email || '-'}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Email</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {(expiredDocs > 0 || expiringDocs > 0 || expiredTrainings > 0 || expiringTrainings > 0) && (
        <div className="alert-banner warning mb-6">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Scadenze da gestire</p>
              <ul className="text-sm mt-1 space-y-1">
                {expiredDocs > 0 && <li>• {expiredDocs} documenti scaduti</li>}
                {expiringDocs > 0 && <li>• {expiringDocs} documenti in scadenza</li>}
                {expiredTrainings > 0 && <li>• {expiredTrainings} formazioni scadute</li>}
                {expiringTrainings > 0 && <li>• {expiringTrainings} formazioni in scadenza</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="tab-documents">
            <FileText className="w-4 h-4" />
            Documenti ({documents.length})
          </TabsTrigger>
          <TabsTrigger value="training" className="flex items-center gap-2" data-testid="tab-training">
            <GraduationCap className="w-4 h-4" />
            Formazione ({trainings.length})
          </TabsTrigger>
          <TabsTrigger value="checks" className="flex items-center gap-2" data-testid="tab-checks">
            <ClipboardCheck className="w-4 h-4" />
            Controlli ({checks.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="documents" className="mt-6">
          <DocumentsList documents={documents} pdvId={id} />
        </TabsContent>
        <TabsContent value="training" className="mt-6">
          <TrainingsList trainings={trainings} />
        </TabsContent>
        <TabsContent value="checks" className="mt-6">
          <ChecksList checks={checks} />
        </TabsContent>
      </Tabs>

      {/* Manager Info */}
      {pdv.manager_name && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-sm font-heading">Responsabile / Legale Rappresentante</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="font-medium" data-testid="pdv-manager-name">{pdv.manager_name}</p>
            {(pdv.manager_birth_place || pdv.manager_birth_date) && (
              <p className="text-sm text-muted-foreground" data-testid="pdv-manager-birth">
                {pdv.manager_birth_place && <>Nato a <span className="font-medium">{pdv.manager_birth_place}</span></>}
                {pdv.manager_birth_date && <> il <span className="font-medium">{(() => {
                  const s = String(pdv.manager_birth_date).trim();
                  // ISO yyyy-MM-dd → gg/mm/aaaa
                  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
                    const [y, m, d] = s.slice(0, 10).split('-');
                    return `${d}/${m}/${y}`;
                  }
                  return s;
                })()}</span></>}
              </p>
            )}
            {pdv.notes && (
              <p className="text-sm text-muted-foreground mt-2">{pdv.notes}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* v102 — Sede Legale (mostrata solo se differisce dalla sede operativa) */}
      {pdv.sede_legale_coincide === false && (pdv.sede_legale_indirizzo || pdv.sede_legale_citta) && (
        <Card className="mt-4 border-amber-300 bg-amber-50/40">
          <CardHeader>
            <CardTitle className="text-sm font-heading">Sede Legale (diversa dalla sede operativa)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              <strong>{pdv.sede_legale_indirizzo}</strong>{pdv.sede_legale_cap && `, ${pdv.sede_legale_cap}`} {pdv.sede_legale_citta} {pdv.sede_legale_provincia && `(${pdv.sede_legale_provincia})`}
            </p>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
